/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { type PropsWithChildren } from 'react';

import { jsx } from '@compiled/react';

import { FlagsProvider } from '@atlaskit/flag';
import { Box } from '@atlaskit/primitives/compiled';
import UFOSegment from '@atlaskit/react-ufo/segment';

import { WidthObserverElement } from '../../../common/ui/width-observer';
import { serve3PBulkConnectWithoutExposure } from '../../../common/utils/experiments';

import { Body } from './body';
import Header from './header';
import HeaderSideBySide from './header-side-by-side';

type Components = {
	input: JSX.Element;
	filters: JSX.Element;
	results: JSX.Element;
	smartAnswers: JSX.Element;
	oauthContainer: JSX.Element;
	rightPanel: JSX.Element;
	autoCorrection: JSX.Element;
	messageBanner?: JSX.Element;
	bulkConnectModal?: JSX.Element;
};

type LayoutProps = {
	components: Components;
};

export const LayoutContent = ({
	components: {
		input,
		filters,
		results,
		smartAnswers,
		oauthContainer,
		rightPanel,
		autoCorrection,
		messageBanner,
		bulkConnectModal,
	},
}: PropsWithChildren<LayoutProps>) => {
	return (
		<WidthObserverElement id="layout">
			<Box testId="search-page-layout">
				<Header components={{ input, filters }} />
				<Body
					components={{
						results,
						smartAnswers,
						rightPanel,
						oauthContainer,
						autoCorrection,
						messageBanner,
					}}
				/>
				{serve3PBulkConnectWithoutExposure() && bulkConnectModal}
			</Box>
		</WidthObserverElement>
	);
};

export const Layout = (props: PropsWithChildren<LayoutProps>) => (
	<UFOSegment name="search-page-layout">
		<FlagsProvider>
			<LayoutContent components={props.components} />
		</FlagsProvider>
	</UFOSegment>
);

type LayoutSideBySideProps = {
	components: {
		filters: JSX.Element;
		results: JSX.Element;
		smartAnswers: JSX.Element;
		oauthContainer: JSX.Element;
		autoCorrection: JSX.Element;
	};
};

export const LayoutSideBySide = ({
	components: { filters, results, smartAnswers, oauthContainer, autoCorrection },
}: PropsWithChildren<LayoutSideBySideProps>) => (
	<UFOSegment name="search-page-sbs-layout">
		<Box testId="search-page-layout">
			<HeaderSideBySide components={{ filters }} />
			<Body components={{ results, smartAnswers, oauthContainer, autoCorrection }} />
		</Box>
	</UFOSegment>
);
